<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useRouter } from 'vue-router'
import { type AutoMigrationListItem, autoMigrationListApi } from '@/api/entities/review/autoMigrationList'

definePage({
  meta: {
    title: '정기이관 목록 관리',
    action: 'read',
    subject: 'review',
  },
})

const router = useRouter()

// 상태
const loading = ref(false)
const items = ref<AutoMigrationListItem[]>([])
const total = ref(0)
const page = ref(1)
const perPage = ref(20)
const searchQuery = ref('')
const sortBy = ref<{ key: string; order: 'asc' | 'desc' }[]>([{ key: 'name', order: 'asc' }])

// 테이블 헤더
const headers = [
  { title: '상태', key: 'is_expired', sortable: true, width: '80px' },
  { title: '업체명 ↕', key: 'name', sortable: true },
  { title: '종료일 ↕', key: 'earliest_end_date', sortable: true },
  { title: '채널 수 ↕', key: 'monthly_channel_count', sortable: true, align: 'end' as const },
  { title: '매핑 수 ↕', key: 'mapping_count', sortable: true, align: 'end' as const },
  { title: '채널당 매핑 ↕', key: 'mapping_per_channel', sortable: true, align: 'end' as const },
  { title: '채널 상품 수 ↕', key: 'target_count', sortable: true, align: 'end' as const },
  { title: '자사몰 상품 수 ↕', key: 'client_item_count', sortable: true, align: 'end' as const },
  { title: '마지막 수집 ↕', key: 'last_crawled_at', sortable: true },
  { title: '마지막 업로드 ↕', key: 'last_upload_success_at', sortable: true },
  { title: '', key: 'actions', sortable: false, width: '100px' },
]

// 데이터 로드
const loadData = async () => {
  loading.value = true

  try {
    const params: Record<string, unknown> = {
      page: page.value,
      per_page: perPage.value,
    }

    if (searchQuery.value.trim())
      params.query = searchQuery.value.trim()

    // 정렬 파라미터
    if (sortBy.value.length > 0) {
      params.sort_by = sortBy.value[0].key
      params.sort_order = sortBy.value[0].order
    }

    const { success, data } = await autoMigrationListApi.list(params)

    if (success && data) {
      items.value = Array.isArray(data.data) ? data.data : []
      total.value = data.meta?.total || 0
    }
  }
  catch (error) {
    console.error('Failed to load auto migration list:', error)
  }
  finally {
    loading.value = false
  }
}

// 페이지 변경
const onPageChange = (newPage: number) => {
  page.value = newPage
  loadData()
}

// 페이지당 항목 수 변경
const onItemsPerPageChange = (newPerPage: number) => {
  perPage.value = newPerPage
  page.value = 1 // 첫 페이지로 이동
  loadData()
}

// 정렬 변경
const onSortChange = (newSortBy: { key: string; order: 'asc' | 'desc' }[]) => {
  sortBy.value = newSortBy
  page.value = 1 // 첫 페이지로 이동
  loadData()
}

// Debounced 검색
const debouncedSearch = useDebounceFn(() => {
  page.value = 1
  loadData()
}, 300)

// 업체별 관리 페이지로 이동
const goToDetail = (item: AutoMigrationListItem) => {
  router.push({
    name: 'review-auto-migration',
    query: { client_id: item.id },
  })
}

// 날짜 포맷 (시간 포함)
const formatDate = (dateString: string | null): string => {
  if (!dateString)
    return '-'

  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime()))
      return '-'

    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  catch {
    return '-'
  }
}

// 날짜만 포맷 (종료일용)
const formatDateOnly = (dateString: string | null): string => {
  if (!dateString)
    return '-'

  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime()))
      return '-'

    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }
  catch {
    return '-'
  }
}

// 종료일 스타일 클래스
const getEndDateClass = (item: AutoMigrationListItem): string => {
  if (!item.earliest_end_date)
    return 'text-grey'

  if (item.is_expired)
    return 'text-error font-weight-bold'

  return ''
}

// 숫자 포맷
const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined)
    return '0'

  return num.toLocaleString('ko-KR')
}

// 전체 정기이관 일괄 실행
const runAllLoading = ref(false)
const runAllDialog = ref(false)
const runAllResultDialog = ref(false)
const runAllResultMessage = ref('')

const runAllPeriodic = async () => {
  runAllDialog.value = false
  runAllLoading.value = true
  try {
    const response = await autoMigrationListApi.runAllPeriodic()
    if (!response.success || !response.data) {
      const errorMsg = response.error?.message || response.error?.statusMessage || `HTTP ${response.status || 'unknown'}`

      runAllResultMessage.value = `요청 실패: ${errorMsg}`
    }
    else {
      runAllResultMessage.value = response.data.message
    }
  }
  catch (e: unknown) {
    console.error('정기이관 일괄실행 요청 실패:', e)

    const message = e instanceof Error ? e.message : '알 수 없는 오류'

    runAllResultMessage.value = `일괄실행 요청에 실패했습니다: ${message}`
  }
  finally {
    runAllLoading.value = false
    runAllResultDialog.value = true
  }
}

onMounted(() => {
  loadData()
})
</script>

<template>
  <VContainer fluid>
    <!-- 헤더 -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VCard>
          <VCardText class="d-flex align-center justify-space-between">
            <div>
              <h5 class="text-h5 mb-1">
                정기이관 목록 관리
              </h5>
              <p class="text-body-2 text-grey mb-0">
                정기이관 활성 업체 현황을 한눈에 확인합니다.
              </p>
            </div>
            <div class="d-flex align-center gap-2">
              <VChip
                color="primary"
                variant="tonal"
              >
                총 {{ formatNumber(total) }}개 업체
              </VChip>
              <VBtn
                color="primary"
                :loading="runAllLoading"
                :disabled="runAllLoading"
                prepend-icon="tabler-player-play"
                @click="runAllDialog = true"
              >
                전체 정기이관 실행
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 검색 및 테이블 -->
    <VRow>
      <VCol cols="12">
        <VCard>
          <VCardText>
            <!-- 검색 -->
            <div class="d-flex align-center gap-4 mb-4 flex-wrap">
              <VTextField
                v-model="searchQuery"
                density="compact"
                variant="outlined"
                placeholder="업체명 검색..."
                prepend-inner-icon="tabler-search"
                hide-details
                clearable
                style="max-width: 300px"
                @update:model-value="debouncedSearch"
              />
            </div>

            <!-- 테이블 -->
            <VDataTableServer
              :headers="headers"
              :items="items"
              :items-length="total"
              :loading="loading"
              :page="page"
              :items-per-page="perPage"
              :sort-by="sortBy"
              class="elevation-0"
              @update:page="onPageChange"
              @update:items-per-page="onItemsPerPageChange"
              @update:sort-by="onSortChange"
            >
              <!-- 기간만료 상태 -->
              <template #item.is_expired="{ item }">
                <VChip
                  v-if="item.is_expired"
                  color="error"
                  size="small"
                  variant="flat"
                >
                  만료
                </VChip>
                <VChip
                  v-else
                  color="success"
                  size="small"
                  variant="tonal"
                >
                  정상
                </VChip>
              </template>

              <!-- 업체명 -->
              <template #item.name="{ item }">
                <span class="font-weight-medium">{{ item.name }}</span>
              </template>

              <!-- 종료일 -->
              <template #item.earliest_end_date="{ item }">
                <span :class="getEndDateClass(item)">
                  {{ formatDateOnly(item.earliest_end_date) }}
                </span>
              </template>

              <!-- 채널 수 -->
              <template #item.monthly_channel_count="{ item }">
                <VChip
                  v-if="item.monthly_channel_count > 0"
                  color="success"
                  size="small"
                  variant="tonal"
                >
                  {{ formatNumber(item.monthly_channel_count) }}
                </VChip>
                <span
                  v-else
                  class="text-grey"
                >0</span>
              </template>

              <!-- 매핑 수 -->
              <template #item.mapping_count="{ item }">
                {{ formatNumber(item.mapping_count) }}
              </template>

              <!-- 채널당 매핑 -->
              <template #item.mapping_per_channel="{ item }">
                <span :class="item.mapping_per_channel > 0 ? '' : 'text-grey'">
                  {{ item.mapping_per_channel?.toFixed(1) ?? '0.0' }}
                </span>
              </template>

              <!-- 채널 상품 수 -->
              <template #item.target_count="{ item }">
                {{ formatNumber(item.target_count) }}
              </template>

              <!-- 자사몰 상품 수 -->
              <template #item.client_item_count="{ item }">
                {{ formatNumber(item.client_item_count) }}
              </template>

              <!-- 마지막 수집 일자 -->
              <template #item.last_crawled_at="{ item }">
                <span :class="item.last_crawled_at ? '' : 'text-grey'">
                  {{ formatDate(item.last_crawled_at) }}
                </span>
              </template>

              <!-- 마지막 업로드 성공 일자 -->
              <template #item.last_upload_success_at="{ item }">
                <span :class="item.last_upload_success_at ? '' : 'text-grey'">
                  {{ formatDate(item.last_upload_success_at) }}
                </span>
              </template>

              <!-- 액션 -->
              <template #item.actions="{ item }">
                <VBtn
                  variant="text"
                  color="primary"
                  size="small"
                  @click="goToDetail(item)"
                >
                  상세
                  <VIcon
                    icon="tabler-chevron-right"
                    size="16"
                  />
                </VBtn>
              </template>
            </VDataTableServer>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 전체 정기이관 실행 확인 다이얼로그 -->
    <VDialog
      v-model="runAllDialog"
      max-width="450"
    >
      <VCard>
        <VCardTitle>전체 정기이관 실행</VCardTitle>
        <VCardText>
          오늘 날짜 기준 활성 상태인 모든 정기이관을 실행합니다. 계속하시겠습니까?
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="runAllDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            @click="runAllPeriodic"
          >
            실행
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 실행 결과 다이얼로그 -->
    <VDialog
      v-model="runAllResultDialog"
      max-width="450"
    >
      <VCard>
        <VCardTitle>정기이관 실행</VCardTitle>
        <VCardText>
          <p class="text-body-1">
            {{ runAllResultMessage }}
          </p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="runAllResultDialog = false"
          >
            확인
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VContainer>
</template>
